import {
    getDocumentSectionTypes,
    updateDocumentSectionType,
} from "@/api/document-section-types/document-section-types";
import { IDocumentSectionType } from "@/api/document-section-types/document-section-types.types";
import { SearchField } from "@/components/admin";
import { Modal } from "@/components/shared/Modal";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { Edit as EditIcon } from "@mui/icons-material";
import { IconButton, TextField, Tooltip } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

const getColumns = (
    onEdit: (row: IDocumentSectionType) => void
): GridColDef<IDocumentSectionType>[] => [
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 1,
        minWidth: 100,
    },
    {
        field: "description",
        headerName: "Description",
        display: "flex",
        flex: 2,
        minWidth: 200,
    },
    {
        field: "actions",
        headerName: "Actions",
        display: "flex",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Tooltip title="Edit description">
                <IconButton
                    size="small"
                    onClick={() => onEdit(params.row)}
                    sx={{ color: "text.secondary" }}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        ),
    },
];

export const DocumentSectionTypesTable = () => {
    const queryClient = useQueryClient();
    const [rows, setRows] = useState<IDocumentSectionType[]>([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingType, setEditingType] = useState<IDocumentSectionType | null>(
        null
    );
    const [editDescription, setEditDescription] = useState("");

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "name", sort: "asc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-document-section-types",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getDocumentSectionTypes(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    const updateMutation = useMutation({
        mutationFn: (data: { id: number; description: string }) =>
            updateDocumentSectionType(data.id, {
                description: data.description,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-document-section-types"],
            });
            handleCloseModal();
        },
    });

    const handleEdit = (row: IDocumentSectionType) => {
        setEditingType(row);
        setEditDescription(row.description || "");
        setEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setEditingType(null);
        setEditDescription("");
    };

    const handleSave = () => {
        if (editingType) {
            updateMutation.mutate({
                id: editingType.id,
                description: editDescription,
            });
        }
    };

    const columns = useMemo(() => getColumns(handleEdit), []);

    return (
        <>
            <div className="mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search document section types..."
                />
            </div>
            <Table
                loading={isFetching}
                columns={columns}
                rows={rows}
                disableColumnFilter
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                rowCount={rowCount}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
            />

            <Modal
                open={editModalOpen}
                onClose={handleCloseModal}
                title="Edit Document Section Type"
                subtitle={editingType?.name}
                maxWidth="md"
                isLoading={updateMutation.isPending}
                actions={[
                    {
                        label: "Save",
                        onClick: handleSave,
                        disabled: updateMutation.isPending,
                    },
                ]}
            >
                <TextField
                    label="Description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    multiline
                    rows={6}
                    fullWidth
                    disabled={updateMutation.isPending}
                    helperText="Describe the characteristics and identifying phrases for this section type"
                />
            </Modal>
        </>
    );
};
